import React, {useEffect, useRef, useState} from 'react';
import {
	Award,
	Briefcase,
	Camera,
	CheckCircle,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Clock,
	Download,
	Edit,
	Facebook,
	FileText,
	Globe,
	Heart,
	Instagram,
	Linkedin,
	MapPin,
	MessageCircle,
	Navigation,
	Phone,
	Plus,
	Quote,
	ShieldCheck,
	ShoppingBag,
	Sparkles,
	Star,
	Store,
	Twitter,
	User,
	Youtube
} from 'lucide-react';

import {Header} from "@/components/Header";

// 🔥 GLOBAL FALLBACK IMAGE
const FALLBACK_IMAGE = '/other_imgs/logo-joglekar kul-72x72.png';

// --- REUSABLE COMPONENTS ---
const BentoCard = ({children, className = "", variant = "default"}) => {
	const baseClasses = "rounded-3xl shadow-sm border flex flex-col transition-all duration-300 font-custom-regular relative";
	const variants = {
		default: "bg-white border-slate-200",
		stone: "bg-gradient-to-br from-stone-50/80 to-white border-stone-200/60",
		blue: "bg-gradient-to-br from-blue-50/50 to-white border-blue-200/60",
		orange: "bg-gradient-to-br from-[#FFF9F2] to-white border-orange-200/60",
		emerald: "bg-gradient-to-br from-emerald-50/50 to-white border-emerald-200/60",
		purple: "bg-gradient-to-br from-purple-50/40 to-white border-purple-200/60",
		rose: "bg-gradient-to-br from-rose-50/50 to-white border-rose-200/60",
		authority: "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-800 text-white"
	};
	return (
		<div className={`${baseClasses} ${variants[variant] || variants.default} ${className}`}>
			{children}
		</div>
	);
};

// Updated CardHeader to include a fixed Edit button
const CardHeader = ({
	                    icon: Icon,
	                    title,
	                    subtitle,
	                    colorClass,
	                    iconColorClass,
	                    borderClass,
	                    rightElement,
	                    onEdit,
	                    isOwner
                    }) => (
	<div
		className={`flex items-center justify-between border-b ${borderClass} pb-3 mb-3 relative z-10 w-full shrink-0`}>
		<div className="flex items-center gap-2.5 min-w-0">
			<div className={`p-2 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border ${borderClass} shrink-0`}>
				<Icon className={`w-4 h-4 ${iconColorClass}`}/>
			</div>
			<div className="flex flex-col min-w-0 shrink-0">
				<h2 className="text-[0.875rem] font-custom-bold text-slate-900 leading-tight mb-0.5 truncate">{title}</h2>
				{subtitle && (
					<div
						className={`text-[0.5625rem] ${colorClass} font-custom-bold uppercase tracking-wider flex items-center gap-1 truncate`}>
						{subtitle}
					</div>
				)}
			</div>
		</div>
		<div className="flex items-center gap-2 shrink-0 pl-2">
			{rightElement}
			{isOwner && onEdit && (
				<button
					onClick={(e) => {
						e.stopPropagation();
						onEdit();
					}}
					className="p-1.5 bg-white text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg shadow-sm transition-all outline-none focus:ring-2 focus:ring-blue-400"
					title={`Edit ${title}`}
				>
					<Edit className="w-4 h-4"/>
				</button>
			)}
		</div>
	</div>
);

const EmptyStateFallback = ({message}) => (
	<div
		className="w-full h-full flex flex-col items-center justify-center opacity-100 select-none py-4 min-h-[6.25rem] flex-1">
		<img src={FALLBACK_IMAGE} alt="Empty Placeholder" className="w-10 h-10 object-contain mb-2 drop-shadow-sm"/>
		<p className="text-[0.625rem] font-custom-bold text-slate-500 uppercase tracking-widest text-center">{message}</p>
	</div>
);

const ScrollArrows = ({sliderRef}) => {
	const [isScrollable, setIsScrollable] = useState(false);

	useEffect(() => {
		const checkScroll = () => {
			if (sliderRef.current) setIsScrollable(sliderRef.current.scrollWidth > sliderRef.current.clientWidth);
		};
		checkScroll();
		window.addEventListener('resize', checkScroll);
		const timer = setTimeout(checkScroll, 500);
		return () => {
			window.removeEventListener('resize', checkScroll);
			clearTimeout(timer);
		};
	}, [sliderRef]);

	if (!isScrollable) return null;

	return (
		<div className="flex gap-1.5 shrink-0">
			<button onClick={() => sliderRef.current?.scrollBy({left: -300, behavior: 'smooth'})}
			        className="p-1.5 bg-white/80 backdrop-blur-sm border border-purple-200 text-purple-600 hover:bg-purple-100 rounded-lg shadow-sm transition-all z-20 relative">
				<ChevronLeft className="w-4 h-4"/>
			</button>
			<button onClick={() => sliderRef.current?.scrollBy({left: 300, behavior: 'smooth'})}
			        className="p-1.5 bg-white/80 backdrop-blur-sm border border-purple-200 text-purple-600 hover:bg-purple-100 rounded-lg shadow-sm transition-all z-20 relative">
				<ChevronRight className="w-4 h-4"/>
			</button>
		</div>
	);
};

const TagPill = ({text, color = "slate"}) => {
	const colors = {
		orange: "bg-orange-50 text-orange-700 border-orange-100",
		slate: "bg-slate-50 text-slate-700 border-slate-200"
	};
	return <span
		className={`px-2.5 py-1 text-[0.625rem] uppercase font-custom-bold tracking-wider rounded-full border ${colors[color] || colors.slate}`}>{text}</span>;
};

// =========================================================================
// MAIN UNIFIED COMPONENT
// =========================================================================

export const YellowPageEntityProfile = ({entityData: propEntityData, isOwner = true}) => {
	const [isHoursExpanded, setIsHoursExpanded] = useState(false);
	const [activeTestimonial, setActiveTestimonial] = useState(0);
	const sliderRef = useRef(null);

	// DUMMY DATA FOR DEMONSTRATION
	const hybridEntity = {
		name: 'Royal Maratha Weddings & Decor',
		tagline: 'Crafting unforgettable traditional & modern celebrations.',
		category: 'Events & Leisure',
		ownerName: 'Mrs. Pallavi Kadam',
		nativePlace: 'Satara',
		rating: 4.9,
		reviewCount: 215,
		isVerified: true,
		entityType: 'both',
		isPhysical: true,
		hasBrochure: true,
		mobile: '+91 98765 44444',
		whatsapp: '+91 98765 44444',
		website: 'www.royalmarathaweddings.in',
		socials: {instagram: 'https://instagram.com', youtube: 'https://youtube.com'},
		description: 'We are a premier event management studio specializing in authentic Maharashtrian weddings. From finding the perfect venue to executing flawless Peshwai decor.',
		portfolio: [], // Empty array to demonstrate the actionable empty state!
		services: [
			'Complete Venue Decoration', 'Photography Teams', 'Catering Coordination',
			'Guest Logistics & Stay', 'Haldi & Mehendi Setup'
		],
		highlights: ['Custom Themes', 'In-house Catering', 'End-to-End Management', 'Experienced Team'],
		testimonials: [
			{
				quote: "Pallavi and her team brought our dream Peshwai wedding to life.",
				author: "Sneha & Rohan",
				title: "Happy Couple",
				rating: 5
			}
		],
		address: 'Office No. 12, Goldfinch Commercial Complex, Shivaji Nagar, Pune',
		operatingHours: {
			monday: '10:00 AM - 7:00 PM',
			tuesday: '10:00 AM - 7:00 PM',
			wednesday: '10:00 AM - 7:00 PM',
			thursday: '10:00 AM - 7:00 PM',
			friday: '10:00 AM - 7:00 PM',
			saturday: '10:00 AM - 4:00 PM',
			sunday: 'Closed'
		},
		brochure: {title: 'Wedding Packages 2024', size: '4.5 MB', url: '#'}
	};

	const entityData = propEntityData || hybridEntity;

	const handleEdit = (section) => {
		console.log(`Open Edit Modal for: ${section}`);
	};

	const ActionableEmptyState = ({message, actionText, onAction, icon: Icon = Plus}) => {
		if (!isOwner) return null;
		return (
			<div onClick={(e) => {
				e.stopPropagation();
				onAction();
			}}
			     className="border-2 border-dashed border-slate-300 p-6 text-center rounded-xl cursor-pointer hover:bg-slate-50 hover:border-slate-400 transition-colors w-full flex flex-col items-center justify-center min-h-[140px] flex-1">
				<Icon className="w-8 h-8 text-slate-400 mb-2"/>
				<p className="text-[0.75rem] font-custom-medium text-slate-500 mb-3 px-4">{message}</p>
				<span
					className="text-[0.6875rem] font-custom-bold text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full border border-blue-100">{actionText}</span>
			</div>
		);
	};

	const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
	const currentDayName = days[new Date().getDay()];
	const isOpenNow = () => {
		if (!entityData.operatingHours) return false;
		const currentHours = entityData.operatingHours[currentDayName];
		return currentHours && currentHours !== 'Closed';
	};
	const getHoursTitle = (cat) => cat === 'Beauty & Health' ? 'Clinic Timings' : cat === 'Shopping' || cat === 'Food & Beverages' ? 'Store Hours' : 'Operating Hours';

	const getHighlightsMeta = (type) => {
		if (type === 'product') return {
			title: 'Why Choose Us',
			subtitle: 'Product Highlights',
			icon: Sparkles,
			colorClass: 'text-purple-600',
			borderClass: 'border-purple-200/60',
			bg: 'bg-purple-50/30'
		};
		if (type === 'service') return {
			title: 'Key Differentiators',
			subtitle: 'Our Guarantees',
			icon: ShieldCheck,
			colorClass: 'text-blue-600',
			borderClass: 'border-blue-200/60',
			bg: 'bg-blue-50/30'
		};
		return {
			title: 'Our Strengths',
			subtitle: 'Value Proposition',
			icon: Award,
			colorClass: 'text-orange-600',
			borderClass: 'border-orange-200/60',
			bg: 'bg-orange-50/30'
		};
	};

	const handleImageError = (e) => {
		e.target.onerror = null;
		e.target.src = FALLBACK_IMAGE;
		e.target.className = "absolute inset-0 w-full h-full object-contain p-8 opacity-100 transition-transform duration-700";
	};

	useEffect(() => {
		if (!entityData.testimonials || entityData.testimonials.length <= 1) return;
		const interval = setInterval(() => {
			setActiveTestimonial((prev) => (prev + 1) % entityData.testimonials.length);
		}, 5000);
		return () => clearInterval(interval);
	}, [entityData.testimonials]);

	const SocialIcon = ({type, url}) => {
		const icons = {
			instagram: <Instagram className="w-4 h-4"/>,
			linkedin: <Linkedin className="w-4 h-4"/>,
			facebook: <Facebook className="w-4 h-4"/>,
			twitter: <Twitter className="w-4 h-4"/>,
			youtube: <Youtube className="w-4 h-4"/>
		};
		if (!icons[type]) return null;
		return (
			<a href={url} target="_blank" rel="noreferrer"
			   className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-stone-200 text-stone-600 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200 transition-colors shadow-sm shrink-0">
				{icons[type]}
			</a>
		);
	};

	return (
		<div className="min-h-screen w-full bg-[#F3F4F6] relative flex flex-col items-center font-custom-regular">
			<Header/>

			<main
				className="w-full 2xl:max-w-[1250px] mx-auto flex flex-col gap-4 px-[0.75rem] sm:px-[1.5rem] pb-12 pt-[4.5rem] md:pt-[5rem] lg:grid lg:grid-cols-12 lg:items-start lg:gap-4">

				{/* =================================================================================
                    LEFT COLUMN: LOGISTICS & ACTION HUB
                   ================================================================================= */}
				<div className="lg:col-span-4 flex flex-col gap-4 lg:sticky lg:top-[5rem] h-max z-10">

					{/* MAIN IDENTITY CARD */}
					<BentoCard variant="stone" className="items-center text-center relative pb-5 overflow-hidden">

						{/* Fixed Main Edit Button */}
						{isOwner && (
							<button
								onClick={() => handleEdit('primary-info')}
								className="absolute top-4 right-4 z-20 p-2 bg-white/90 backdrop-blur-sm text-slate-500 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg shadow-sm transition-all outline-none focus:ring-2 focus:ring-blue-400"
								title="Edit Basic Info"
							>
								<Edit className="w-4 h-4"/>
							</button>
						)}

						<div
							className="w-full h-20 bg-gradient-to-r from-stone-200/50 to-stone-100/50 absolute top-0 left-0 z-0"></div>

						<div className="relative mb-3 mt-8 z-10 shrink-0">
							<div
								className="w-20 h-20 rounded-full bg-white p-1 shadow-sm border border-stone-200 mx-auto relative">
								<div
									className="w-full h-full rounded-full bg-stone-50 flex items-center justify-center overflow-hidden p-2">
									{entityData.profileImage ? (
										<img src={entityData.profileImage} alt="Profile"
										     className="w-full h-full object-cover rounded-full"
										     onError={handleImageError}/>
									) : (
										<img src={FALLBACK_IMAGE} alt="Default Profile"
										     className="w-full h-full object-contain opacity-100"/>
									)}
								</div>
							</div>
							{entityData.isVerified && (
								<div
									className="absolute bottom-0 right-0 bg-blue-500 p-1 rounded-full shadow-md border-2 border-white">
									<CheckCircle className="w-3.5 h-3.5 text-white fill-none stroke-[3px]"/>
								</div>
							)}
						</div>

						<div className="px-5 flex flex-col items-center w-full z-10">
							<h1 className="text-lg font-custom-bold text-slate-900 leading-tight mb-1">{entityData.name}</h1>
							<p className="text-[0.6875rem] text-slate-500 font-custom-medium italic mb-3">"{entityData.tagline}"</p>

							<div className="flex flex-wrap items-center justify-center gap-2 mb-4">
								<TagPill text={entityData.category} color="slate"/>
								<TagPill text={entityData.entityType} color="orange"/>
							</div>
						</div>

						<div className="px-5 w-full">
							<div
								className="w-full bg-white/60 backdrop-blur-sm p-4 rounded-xl border border-stone-200/60 mb-5 text-left shadow-sm shrink-0">
								<p className="text-[0.5625rem] font-custom-bold text-stone-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
									<Store className="w-3 h-3"/> About Us
								</p>
								<p className="text-[0.75rem] text-slate-600 leading-relaxed font-custom-medium">
									{entityData.description || (isOwner ? "Write a short description to tell the community about what you do." : "")}
								</p>
							</div>
						</div>

						<div className="px-5 w-full">
							<div className="w-full flex flex-col gap-2.5 shrink-0">
								<a href={`tel:${entityData.mobile}`}
								   className="flex items-center justify-center gap-2 w-full py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl shadow-md transition-all active:scale-95">
									<Phone className="w-4 h-4 fill-current"/>
									<span className="font-custom-bold text-[0.8125rem]">Call Now</span>
								</a>
								<div className="grid grid-cols-2 gap-2.5">
									{entityData.whatsapp ? (
										<a href={`https://wa.me/${entityData.whatsapp}`} target="_blank"
										   rel="noreferrer"
										   className="flex items-center justify-center gap-2 py-2 bg-white text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-xl border border-stone-200 shadow-sm transition-all font-custom-bold">
											<MessageCircle className="w-4 h-4"/>
											<span className="text-[0.6875rem]">WhatsApp</span>
										</a>
									) : (
										<div
											className="flex items-center justify-center gap-2 py-2 bg-slate-50 text-slate-400 rounded-xl border border-slate-200 font-custom-bold cursor-not-allowed">
											<MessageCircle className="w-4 h-4"/>
											<span className="text-[0.6875rem]">No WhatsApp</span>
										</div>
									)}
									{entityData.website ? (
										<a href={`https://${entityData.website}`} target="_blank" rel="noreferrer"
										   className="flex items-center justify-center gap-2 py-2 bg-white text-slate-700 hover:bg-slate-100 rounded-xl border border-stone-200 shadow-sm transition-all font-custom-bold">
											<Globe className="w-4 h-4 text-slate-500"/>
											<span className="text-[0.6875rem]">Website</span>
										</a>
									) : (
										<div
											className="flex items-center justify-center gap-2 py-2 bg-slate-50 text-slate-400 rounded-xl border border-slate-200 font-custom-bold cursor-not-allowed">
											<Globe className="w-4 h-4"/>
											<span className="text-[0.6875rem]">No Website</span>
										</div>
									)}
								</div>
							</div>

							<div
								className="w-full mt-5 pt-4 border-t border-stone-200 flex justify-center gap-2 min-h-[48px]">
								{entityData.socials && Object.keys(entityData.socials).length > 0 ? (
									Object.entries(entityData.socials).map(([platform, url]) => (
										<SocialIcon key={platform} type={platform} url={url}/>
									))
								) : (
									isOwner &&
									<p className="text-[0.65rem] text-slate-400 font-custom-medium mt-1">No social links
										added.</p>
								)}
							</div>
						</div>
					</BentoCard>

					<BentoCard variant="blue" className="flex flex-col divide-y divide-blue-100/50">

						<div className="p-4 shrink-0">
							<CardHeader isOwner={isOwner} onEdit={() => handleEdit('logistics')} icon={MapPin}
							            title="Logistics"
							            subtitle={entityData.isPhysical ? "Physical Location" : "Service Area"}
							            colorClass="text-blue-600" iconColorClass="text-blue-600"
							            borderClass="border-blue-200/50"/>
							{entityData.isPhysical ? (
								<div>
									<p className="text-[0.75rem] font-custom-medium text-slate-800 leading-snug mb-3 ml-1">{entityData.address}</p>
									<button
										className="w-full py-2 bg-white hover:bg-blue-50 text-blue-700 text-[0.6875rem] font-custom-bold rounded-lg border border-blue-200 shadow-sm flex items-center justify-center gap-1.5 transition-colors">
										<Navigation className="w-3 h-3 text-blue-500"/> Get Directions
									</button>
								</div>
							) : (
								<div className="flex items-start gap-2.5 ml-1">
									<div className="p-1.5 bg-blue-100/50 rounded-lg text-blue-600 mt-0.5"><Globe
										className="w-3 h-3"/></div>
									<div>
										<p className="text-[0.5625rem] text-blue-500 font-custom-bold uppercase tracking-wider">Coverage
											Area</p>
										<p className="text-[0.6875rem] font-custom-bold text-slate-900">{entityData.reach?.serviceArea || entityData.serviceArea || "Not specified"}</p>
									</div>
								</div>
							)}
						</div>

						{((entityData.operatingHours && Object.keys(entityData.operatingHours).length > 0) || isOwner) && (
							<div className="p-4 bg-blue-50/30 shrink-0 relative">
								{isOwner && (
									<button onClick={() => handleEdit('hours')}
									        className="absolute top-4 right-4 p-1.5 bg-white text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg shadow-sm transition-all z-20">
										<Edit className="w-4 h-4"/>
									</button>
								)}
								{entityData.operatingHours ? (
									<>
										<div className="flex items-center justify-between cursor-pointer group pr-10"
										     onClick={() => setIsHoursExpanded(!isHoursExpanded)}>
											<div className="ml-1">
												<p className="text-[0.5625rem] font-custom-bold text-blue-500 uppercase tracking-widest flex items-center gap-1.5 mb-1">
													<Clock className="w-3 h-3"/> {getHoursTitle(entityData.category)}
												</p>
												<div className="flex items-center gap-2">
                                                    <span
	                                                    className={`px-2 py-0.5 text-[0.5rem] uppercase font-custom-bold rounded-full border ${isOpenNow() ? 'bg-green-100/50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                                        {isOpenNow() ? 'Open Now' : 'Closed'}
                                                    </span>
													<span className="text-[0.6875rem] font-custom-bold text-slate-800">
                                                        Today: {entityData.operatingHours?.[currentDayName] || 'Not Set'}
                                                    </span>
												</div>
											</div>
											<div
												className="p-1 rounded-full bg-white shadow-sm border border-blue-200 group-hover:border-blue-300 transition-colors">
												<ChevronDown
													className={`w-3.5 h-3.5 text-blue-500 transition-transform duration-300 ${isHoursExpanded ? 'rotate-180' : ''}`}/>
											</div>
										</div>

										<div
											className={`grid transition-[grid-template-rows,opacity] duration-500 ease-in-out ${isHoursExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
											<div className="overflow-hidden">
												<div
													className="flex flex-col gap-1.5 pt-3 mt-3 border-t border-blue-200/50 ml-1">
													{Object.entries(entityData.operatingHours).map(([day, time]) => (
														<div key={day}
														     className={`flex justify-between items-center text-[0.6875rem] ${day === currentDayName ? 'font-custom-bold text-blue-700' : 'text-slate-600'}`}>
															<span className="capitalize">{day}</span>
															<span className="font-custom-medium">{time}</span>
														</div>
													))}
												</div>
											</div>
										</div>
									</>
								) : (
									<ActionableEmptyState
										message="Add operating hours so customers know when you are available."
										actionText="Add Timings" onAction={() => handleEdit('hours')} icon={Clock}/>
								)}
							</div>
						)}

						{(entityData.hasBrochure || isOwner) && (
							<div className="p-4 shrink-0 mt-auto relative">
								{isOwner && (
									<button onClick={() => handleEdit('brochure')}
									        className="absolute top-4 right-4 p-1.5 bg-white text-slate-400 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 hover:border-blue-200 rounded-lg shadow-sm transition-all z-20">
										<Edit className="w-4 h-4"/>
									</button>
								)}
								{entityData.hasBrochure && entityData.brochure ? (
									<a href={entityData.brochure.url} download
									   className="flex items-center justify-between w-full p-2.5 bg-gradient-to-r from-blue-50 to-blue-50/20 hover:from-blue-100 hover:to-blue-50 border border-blue-100 rounded-xl transition-colors group mt-8">
										<div className="flex items-center gap-2.5">
											<FileText className="w-4 h-4 text-blue-600"/>
											<div className="text-left">
												<p className="text-[0.6875rem] font-custom-bold text-blue-900 leading-tight">Download
													Brochure</p>
											</div>
										</div>
										<Download
											className="w-3.5 h-3.5 text-blue-500 group-hover:scale-110 transition-transform"/>
									</a>
								) : (
									<ActionableEmptyState message="Upload a PDF menu or catalog."
									                      actionText="Add Brochure"
									                      onAction={() => handleEdit('brochure')} icon={FileText}/>
								)}
							</div>
						)}
					</BentoCard>
				</div>


				{/* =================================================================================
                    RIGHT COLUMN: VALUE & TRUST HUB
                   ================================================================================= */}
				<div className="lg:col-span-8 flex flex-col gap-4 h-max lg:sticky lg:top-[5rem]">

					{/* NATIVE BENTO PROFILE STRENGTH INDICATOR */}
					{isOwner && (
						<BentoCard variant="stone"
						           className="p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
							<div className="flex items-center gap-3 w-full sm:w-auto">
								<div
									className="w-10 h-10 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
									<Award className="w-5 h-5 text-blue-600"/>
								</div>
								<div>
									<h3 className="text-[0.875rem] font-custom-bold text-slate-900 leading-tight">Profile
										Strength: Intermediate</h3>
									<p className="text-[0.6875rem] text-slate-500 font-custom-medium mt-0.5">Add a photo
										gallery to attract 3x more customers.</p>
								</div>
							</div>
							<div className="w-full sm:w-1/3 flex items-center gap-3 shrink-0">
								<div className="flex-1 h-2.5 bg-slate-200 rounded-full overflow-hidden shadow-inner">
									<div
										className="h-full bg-blue-500 rounded-full w-[60%] transition-all duration-1000 ease-out"></div>
								</div>
								<span className="text-[0.75rem] font-custom-bold text-slate-700">60%</span>
							</div>
						</BentoCard>
					)}

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<BentoCard variant="orange"
						           className="p-4 md:p-5 relative overflow-hidden flex flex-col justify-start h-auto md:h-[14.5rem]">
							<User
								className="absolute -right-8 -bottom-8 w-40 h-40 text-orange-500 opacity-[0.03] rotate-12 pointer-events-none"/>
							<CardHeader isOwner={isOwner} onEdit={() => handleEdit('owner-info')} icon={Store}
							            title="Community Roots"
							            subtitle={<><CheckCircle className="w-2.5 h-2.5"/> Verified Identity</>}
							            colorClass="text-orange-600" iconColorClass="text-orange-600"
							            borderClass="border-orange-200/60"/>

							<div className="grid grid-cols-2 gap-2 relative z-10 mt-1 flex-1">
								<div
									className="bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-orange-100 flex flex-col justify-center">
									<p className="text-[0.5625rem] text-orange-600/80 font-custom-bold uppercase tracking-widest mb-1 flex items-center gap-1">
										<User className="w-2.5 h-2.5"/> Owner</p>
									<p className="text-slate-900 font-custom-bold text-[0.75rem] truncate leading-tight">{entityData.ownerName}</p>
								</div>
								<div
									className="bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-orange-100 flex flex-col justify-center">
									<p className="text-[0.5625rem] text-orange-600/80 font-custom-bold uppercase tracking-widest mb-1 flex items-center gap-1">
										<MapPin className="w-2.5 h-2.5"/> Native Place</p>
									<p className="text-slate-900 font-custom-bold text-[0.75rem] truncate leading-tight">{entityData.nativePlace}</p>
								</div>
								<div
									className="col-span-2 bg-white/80 backdrop-blur-sm rounded-xl p-3 shadow-sm border border-orange-100 flex items-center justify-between">
									<p className="text-[0.5625rem] text-orange-600/80 font-custom-bold uppercase tracking-widest flex items-center gap-1">
										<Star className="w-2.5 h-2.5"/> Trust Rating</p>
									<div className="flex items-center gap-1">
										<span
											className="text-slate-900 font-custom-bold text-[0.875rem] leading-none">{entityData.rating || 'N/A'}</span>
										{entityData.rating > 0 &&
											<Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 -mt-0.5"/>}
										<span
											className="text-[0.625rem] text-slate-500 font-custom-medium ml-0.5">({entityData.reviewCount} Reviews)</span>
									</div>
								</div>
							</div>
						</BentoCard>

						{entityData.entityType === 'service' ? (
							((entityData.services && entityData.services.length > 0) || isOwner) && (
								<BentoCard variant="emerald"
								           className="p-4 md:p-5 flex flex-col h-auto md:h-[14.5rem] relative">
									<CardHeader isOwner={isOwner} onEdit={() => handleEdit('core-services')}
									            icon={Briefcase} title="Core Offerings" subtitle="Primary Services"
									            colorClass="text-emerald-600" iconColorClass="text-emerald-600"
									            borderClass="border-emerald-200/60"/>

									{entityData.services && entityData.services.length > 0 ? (
										<div className="flex flex-col flex-1 overflow-hidden mt-1">
											<div
												className="flex flex-wrap content-start gap-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-2 flex-[0_1_auto] min-h-0">
												{entityData.services.map((item, idx) => (
													<div key={idx}
													     className="w-max group flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-emerald-100 shadow-sm hover:border-emerald-300 transition-all cursor-default">
														<CheckCircle
															className="w-3 h-3 text-emerald-500 group-hover:text-emerald-600 shrink-0"/>
														<span
															className="text-slate-800 font-custom-bold text-[0.6875rem] whitespace-nowrap">{item}</span>
													</div>
												))}
											</div>
											<div
												className="flex-1 min-h-0 flex flex-col items-center justify-center pointer-events-none overflow-hidden">
												<img src={FALLBACK_IMAGE} alt="Placeholder"
												     className="h-[2.5rem] min-h-0 shrink w-auto object-contain mb-1 drop-shadow-sm"/>
											</div>
										</div>
									) : (
										<ActionableEmptyState message="List your primary services here."
										                      actionText="Add Services"
										                      onAction={() => handleEdit('core-services')}
										                      icon={Briefcase}/>
									)}
								</BentoCard>
							)
						) : (
							((entityData.testimonials && entityData.testimonials.length > 0) || isOwner) && (
								<BentoCard variant="rose"
								           className="p-4 md:p-5 flex flex-col justify-between relative h-auto md:h-[14.5rem]">
									<Quote
										className="absolute top-2 right-4 w-12 h-12 text-rose-500/10 z-0 pointer-events-none"/>

									<div className="relative z-10 flex-1 flex flex-col">
										<CardHeader isOwner={isOwner} onEdit={() => handleEdit('reviews')} icon={Heart}
										            title="Client Reviews" subtitle="Community Feedback"
										            colorClass="text-rose-600" iconColorClass="text-rose-600"
										            borderClass="border-rose-200/60"/>

										{entityData.testimonials && entityData.testimonials.length > 0 ? (
											<div className="relative flex-1 overflow-hidden mb-2">
												{entityData.testimonials.map((test, idx) => {
													const isActive = idx === activeTestimonial;
													const rating = test.rating || 5;
													return (
														<div key={idx}
														     className={`absolute inset-0 flex flex-col pointer-events-none ${isActive ? 'z-10' : 'z-0'}`}>
															<div className="flex gap-0.5 mb-1.5 ml-1">
																{[...Array(test.rating)].map((_, i) => (
																	<Star key={i}
																	      className={`w-3 h-3 transition-all duration-500 ease-out transform ${isActive ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-0 -rotate-90'} ${i < rating ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_3px_rgba(250,204,21,0.8)]' : 'text-slate-200'}`}
																	      style={{transitionDelay: `${isActive ? 150 + (i * 75) : 0}ms`}}/>
																))}
															</div>
															<p className={`text-[0.6875rem] text-slate-700 font-custom-medium italic line-clamp-2 leading-relaxed transition-all duration-500 ease-out transform ml-1 pr-4 ${isActive ? 'opacity-100 translate-y-0 delay-[250ms]' : 'opacity-0 translate-y-4'}`}>"{test.quote}"</p>
															<div
																className={`mt-auto pt-2 border-t border-rose-100/50 w-3/4 absolute bottom-0 transition-all duration-500 ease-out transform ml-1 ${isActive ? 'opacity-100 translate-x-0 delay-[400ms]' : 'opacity-0 -translate-x-8'}`}>
																<p className="text-[0.625rem] font-custom-bold text-slate-900">{test.author}</p>
															</div>
														</div>
													);
												})}
											</div>
										) : (
											<ActionableEmptyState
												message="Request reviews from past clients to build trust."
												actionText="Get Reviews" onAction={() => handleEdit('reviews')}
												icon={Heart}/>
										)}
									</div>
									{(!isOwner || (entityData.testimonials && entityData.testimonials.length > 0)) && (
										<button
											className="w-full py-2 bg-white border border-rose-200 hover:border-rose-400 hover:bg-rose-50 text-rose-700 text-[0.6875rem] font-custom-bold rounded-xl transition-colors shadow-sm relative z-10 shrink-0">
											Write a Review
										</button>
									)}
								</BentoCard>
							)
						)}
					</div>

					{/* 🛍️ IF PRODUCT ONLY */}
					{entityData.entityType === 'product' && ((entityData.products && entityData.products.length > 0) || isOwner) && (
						<BentoCard variant="purple" className="p-4 md:p-5 relative flex flex-col">
							<CardHeader isOwner={isOwner} onEdit={() => handleEdit('products')} icon={ShoppingBag}
							            title="Featured Products" subtitle="Swipe to explore"
							            colorClass="text-purple-600" iconColorClass="text-purple-600"
							            borderClass="border-purple-200/60"
							            rightElement={entityData.products?.length > 0 &&
								            <ScrollArrows sliderRef={sliderRef}/>}/>

							{entityData.products && entityData.products.length > 0 ? (
								<div ref={sliderRef}
								     className={`flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 pt-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]`}>
									{entityData.products.map((item, idx) => (
										<div key={idx}
										     className="relative shrink-0 w-[13.75rem] md:w-[15rem] snap-center rounded-2xl overflow-hidden shadow-sm hover:shadow-md border border-purple-100 group bg-slate-50 flex flex-col h-[16.25rem]">
											<img src={item.image} alt="Product"
											     className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${item.isFallback ? 'object-contain p-8' : ''}`}
											     onError={handleImageError}/>
											<div
												className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300"/>
											<div
												className="absolute inset-x-0 bottom-0 p-4 flex flex-col justify-end transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
												<p className="font-custom-bold text-white text-[0.9375rem] leading-tight drop-shadow-md truncate">{item.name}</p>
												{item.subtitle &&
													<p className="text-[0.6875rem] text-slate-300 font-custom-medium mt-1 truncate">{item.subtitle}</p>}
												{item.price &&
													<p className="text-[0.8125rem] font-custom-bold text-orange-400 mt-2.5">{item.price}</p>}
											</div>
										</div>
									))}
								</div>
							) : (
								<ActionableEmptyState message="Showcase your products to attract more customers."
								                      actionText="Add Products" onAction={() => handleEdit('products')}
								                      icon={ShoppingBag}/>
							)}
						</BentoCard>
					)}

					{/* 🤝 IF SERVICE ONLY */}
					{entityData.entityType === 'service' && ((entityData.testimonials?.length > 0 || entityData.credentials?.experience) || isOwner) && (
						<BentoCard variant="authority"
						           className="relative overflow-hidden flex flex-col md:flex-row shadow-lg">
							{isOwner && (
								<button onClick={() => handleEdit('authority')}
								        className="absolute top-4 right-4 z-20 p-1.5 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 border border-slate-600 hover:border-slate-500 rounded-lg shadow-sm transition-all outline-none focus:ring-2 focus:ring-orange-400">
									<Edit className="w-4 h-4"/>
								</button>
							)}
							<div
								className="absolute top-[-20%] right-[-10%] w-80 h-80 bg-orange-500/10 blur-[100px] rounded-full pointer-events-none"/>

							<div
								className="md:w-3/5 p-6 flex flex-col justify-between border-b md:border-b-0 md:border-r border-slate-700/50">
								<div>
									<div className="flex items-center gap-2 mb-5">
										<Quote className="w-6 h-6 text-orange-400 opacity-80"/>
										<span
											className="text-[0.625rem] uppercase font-custom-bold text-slate-400 tracking-widest">Client Feedback</span>
									</div>

									{entityData.testimonials && entityData.testimonials.length > 0 ? (
										<div className="relative h-28 overflow-hidden">
											{entityData.testimonials.map((test, idx) => {
												const isActive = idx === activeTestimonial;
												return (
													<div key={idx}
													     className={`absolute inset-0 pointer-events-none ${isActive ? 'z-10' : 'z-0'}`}>
														<div className="flex gap-0.5 mb-2">
															{[...Array(test.rating)].map((_, i) => (
																<Star key={i}
																      className={`w-3 h-3 transition-all duration-500 ease-out transform ${isActive ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-0 -rotate-90'} ${i < (test.rating || 5) ? 'fill-orange-400 text-orange-400 drop-shadow-[0_0_3px_rgba(251,146,60,0.8)]' : 'text-slate-600'}`}
																      style={{transitionDelay: `${isActive ? 150 + (i * 75) : 0}ms`}}/>
															))}
														</div>
														<p className={`text-[0.875rem] text-slate-200 font-custom-medium leading-relaxed italic line-clamp-2 transition-all duration-500 ease-out transform ${isActive ? 'opacity-100 translate-y-0 delay-[250ms]' : 'opacity-0 translate-y-4'}`}>"{test.quote}"</p>
														<div
															className={`mt-3 transition-all duration-500 ease-out transform ${isActive ? 'opacity-100 translate-x-0 delay-[400ms]' : 'opacity-0 -translate-x-4'}`}>
															<p className="text-white font-custom-bold text-[0.75rem]">{test.author}</p>
															<p className="text-slate-400 text-[0.5625rem] font-custom-medium uppercase tracking-wider">{test.title}</p>
														</div>
													</div>
												);
											})}
										</div>
									) : (
										isOwner ? (
											<div className="h-28 flex flex-col justify-center">
												<p className="text-sm text-slate-400 font-custom-medium mb-3">No
													feedback available.</p>
												<button onClick={() => handleEdit('authority')}
												        className="text-xs font-custom-bold text-orange-400 bg-orange-400/10 px-3 py-1.5 rounded-lg w-max hover:bg-orange-400/20">Add
													Testimonial
												</button>
											</div>
										) : (
											<EmptyStateFallback message="No feedback yet"/>
										)
									)}
								</div>
							</div>

							<div className="md:w-2/5 flex flex-col bg-slate-800/30">
								<div className="p-6 pb-4 border-b border-slate-700/50">
									<p className="text-[0.5625rem] font-custom-bold text-slate-400 uppercase tracking-widest mb-4">Track
										Record</p>

									{(!entityData.credentials?.experience && !entityData.credentials?.primaryMetric) ? (
										isOwner ? (
											<button onClick={() => handleEdit('authority')}
											        className="text-xs font-custom-medium text-slate-300 border border-slate-600 border-dashed rounded-lg w-full py-4 hover:bg-slate-700/50 transition-colors">+
												Add Experience</button>
										) : (
											<EmptyStateFallback message="No track record data"/>
										)
									) : (
										<div className="grid grid-cols-1 gap-4">
											{entityData.credentials?.experience && (
												<div className="flex items-center gap-3">
													<div
														className="p-2 bg-slate-800 rounded-lg text-blue-400 border border-slate-700/50 shadow-sm">
														<Award className="w-4 h-4"/></div>
													<div><p
														className="text-[0.875rem] font-custom-bold text-white leading-tight">{entityData.credentials.experience}</p>
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							</div>
						</BentoCard>
					)}

					{/* 🛍️+🤝 IF HYBRID -> Portfolio Slider + Services side-by-side */}
					{entityData.entityType === 'both' && (
						<div className="grid grid-cols-1 md:grid-cols-5 gap-4">
							{((entityData.portfolio && entityData.portfolio.length > 0) || isOwner) && (
								<BentoCard variant="purple"
								           className="md:col-span-3 p-4 md:p-5 relative flex flex-col h-auto md:h-[24rem]">
									<CardHeader isOwner={isOwner} onEdit={() => handleEdit('portfolio')} icon={Camera}
									            title="Our Portfolio" subtitle="Swipe to explore"
									            colorClass="text-purple-600" iconColorClass="text-purple-600"
									            borderClass="border-purple-200/60"
									            rightElement={entityData.portfolio?.length > 0 &&
										            <ScrollArrows sliderRef={sliderRef}/>}/>

									{entityData.portfolio && entityData.portfolio.length > 0 ? (
										<div ref={sliderRef}
										     className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 pt-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] flex-1">
											{entityData.portfolio.map((item, idx) => (
												<div key={idx}
												     className="relative shrink-0 w-[12.5rem] md:w-[13.75rem] snap-center rounded-2xl overflow-hidden shadow-sm border border-purple-100 group bg-slate-50 flex flex-col h-full min-h-[12.5rem] md:min-h-[13.75rem]">
													<img src={item.image} alt="Portfolio"
													     className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${item.isFallback ? 'object-contain p-8' : ''}`}
													     onError={handleImageError}/>
													<div
														className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-300"/>
													<div
														className="absolute inset-x-0 bottom-0 p-4 flex flex-col justify-end transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
														<p className="font-custom-bold text-white text-[0.8125rem] leading-tight truncate">{item.name}</p>
														{item.subtitle &&
															<p className="text-[0.625rem] text-slate-300 font-custom-medium mt-1 truncate">{item.subtitle}</p>}
													</div>
												</div>
											))}
										</div>
									) : (
										<ActionableEmptyState message="Upload images of your past projects or work."
										                      actionText="Add Portfolio"
										                      onAction={() => handleEdit('portfolio')} icon={Camera}/>
									)}
								</BentoCard>
							)}

							{((entityData.services && entityData.services.length > 0) || isOwner) && (
								<BentoCard
									className="md:col-span-2 p-4 md:p-5 bg-white border-slate-200 flex flex-col h-[17.5rem] md:h-[24rem] relative">
									<CardHeader isOwner={isOwner} onEdit={() => handleEdit('services')} icon={Sparkles}
									            title="All Services" subtitle="Capabilities"
									            colorClass="text-orange-600" iconColorClass="text-orange-600"
									            borderClass="border-slate-100"/>

									{entityData.services && entityData.services.length > 0 ? (
										<div className="flex flex-col flex-1 overflow-hidden mt-1">
											<div
												className="flex flex-wrap content-start gap-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-2 flex-[0_1_auto] min-h-0">
												{entityData.services.map((item, idx) => (
													<div key={idx}
													     className="w-max group flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 shadow-sm hover:border-orange-300 transition-all cursor-default">
														<CheckCircle
															className="w-3 h-3 text-orange-500 group-hover:text-orange-600 shrink-0"/>
														<span
															className="text-slate-700 font-custom-bold text-[0.6875rem] whitespace-nowrap">{item}</span>
													</div>
												))}
											</div>
										</div>
									) : (
										<ActionableEmptyState message="List your services here."
										                      actionText="Add Services"
										                      onAction={() => handleEdit('services')} icon={Briefcase}/>
									)}
								</BentoCard>
							)}
						</div>
					)}

					{((entityData.highlights && entityData.highlights.length > 0) || isOwner) && (
						<BentoCard
							className={`p-4 md:p-5 flex flex-col ${getHighlightsMeta(entityData.entityType).bg} border-slate-200 relative`}>
							<CardHeader isOwner={isOwner} onEdit={() => handleEdit('highlights')}
							            icon={getHighlightsMeta(entityData.entityType).icon}
							            title={getHighlightsMeta(entityData.entityType).title}
							            subtitle={getHighlightsMeta(entityData.entityType).subtitle}
							            colorClass={getHighlightsMeta(entityData.entityType).colorClass}
							            iconColorClass={getHighlightsMeta(entityData.entityType).colorClass}
							            borderClass="border-slate-200"/>

							{entityData.highlights && entityData.highlights.length > 0 ? (
								<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
									{entityData.highlights.map((highlight, idx) => (
										<div key={idx}
										     className="flex flex-col items-center justify-center text-center p-3 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-default group">
											<div
												className="p-1.5 bg-green-50 rounded-full mb-1.5 group-hover:bg-green-100 transition-colors">
												<CheckCircle className="w-4 h-4 text-green-500"/>
											</div>
											<span
												className="text-[0.6875rem] font-custom-bold text-slate-700 leading-tight">{highlight}</span>
										</div>
									))}
								</div>
							) : (
								<ActionableEmptyState
									message={`Add ${getHighlightsMeta(entityData.entityType).subtitle} to stand out.`}
									actionText="Add Highlights" onAction={() => handleEdit('highlights')} icon={Award}/>
							)}
						</BentoCard>
					)}

				</div>
			</main>
		</div>
	);
};